import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  WorkdayEndpointConfig,
  WorkdayIntegrationConfig
} from '@dadohr/shared/domainTypes/integrations/workday';
import styled from '@emotion/styled';
import { Button, Dialog, TextField } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { every, isEqual } from 'lodash';
import { PlusCircle, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import shortid from 'shortid';
import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { CustomInputLabel } from '../../../../../../components/CustomInputLabel';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogContentWithoutPadding } from '../../../../../../components/DialogContentWithoutPadding';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { useUnsafeTranslation } from '../../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../../layout/Flex';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { callFirebaseFunction } from '../../../../../../services/firebaseFunctions';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties,
  IntegrationSetupDialogWithConfigProperties,
  masked
} from '../common';
import { removeWorkdayConfig, useWorkdayConfig } from './service';

const NAME = 'Workday RaaS';

const areEndpointsValidAndChanged = (
  next: WorkdayEndpointConfig[],
  prev: WorkdayEndpointConfig[]
) => {
  if (!next.length || isEqual(next, prev)) {
    return false;
  }
  return every(next, (e) => !!(e.name && e.url && e.username && e.password));
};

const EndpointsContainer = styled(FlexContainerVertical)((p) => ({
  padding: p.theme.spacing(4),
  paddingBottom: p.theme.spacing(2),
  paddingTop: p.theme.spacing(1)
}));

const EndpointContainer = styled(FlexContainerVertical)((p) => ({
  padding: p.theme.spacing(2),
  background: p.theme.custom.colors.gray.w100
}));

type State = {
  endpoints: WorkdayEndpointConfig[];
};

const SetupDialog = ({
  config,
  open,
  onClose,
  onSuccessfulIntegration
}: IntegrationSetupDialogWithConfigProperties<WorkdayIntegrationConfig>) => {
  const { t } = useUnsafeTranslation();

  const { endpoints } = config?.data || {
    endpoints: EMPTY_ARR
  };

  const [state, setState] = useState<State>({
    endpoints: endpoints.map((e) => ({
      ...e,
      password: masked(e.password)
    }))
  });
  useEffect(() => {
    setState({
      endpoints: endpoints.map((e) => ({
        ...e,
        password: masked(e.password)
      }))
    });
  }, [endpoints]);

  if (!config) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <DialogContentWithoutPadding>
        <EndpointsContainer spacing={2} fullWidth>
          <Alert severity="info" icon={false}>
            {t(
              'Connect Dado to Workday using "Reports as a Service" (Raas) to sync data about workers from Workday to Dado.'
            )}
          </Alert>
          {state.endpoints.map((e) => {
            const updateState = (
              fn: (prev: WorkdayEndpointConfig) => WorkdayEndpointConfig
            ) =>
              setState((s) => ({
                ...s,
                endpoints: s.endpoints.map((se) =>
                  se.id === e.id ? fn(se) : se
                )
              }));
            return (
              <EndpointContainer spacing={1} fullWidth key={e.id}>
                <CustomInputLabel
                  label={t('Endpoint Name')}
                  subLabel={t('For information only. Visible only in Dado')}
                  fullWidth
                >
                  <TextField
                    variant="outlined"
                    value={e.name}
                    onChange={(ev) =>
                      updateState((e) => ({
                        ...e,
                        name: ev.target.value
                      }))
                    }
                    fullWidth
                  />
                </CustomInputLabel>

                <CustomInputLabel label={t('Import API URL')} fullWidth>
                  <TextField
                    variant="outlined"
                    value={e.url}
                    onChange={(ev) =>
                      updateState((e) => ({
                        ...e,
                        url: ev.target.value
                      }))
                    }
                    fullWidth
                  />
                </CustomInputLabel>

                <CustomInputLabel label={t('Username')} fullWidth>
                  <TextField
                    variant="outlined"
                    value={e.username}
                    onChange={(ev) =>
                      updateState((e) => ({ ...e, username: ev.target.value }))
                    }
                    fullWidth
                  />
                </CustomInputLabel>
                <CustomInputLabel label={t('Password')} fullWidth>
                  <TextField
                    variant="outlined"
                    value={e.password}
                    onChange={(ev) =>
                      updateState((s) => ({ ...s, password: ev.target.value }))
                    }
                    fullWidth
                  />
                </CustomInputLabel>
                <FlexContainer
                  justifyContent="flex-end"
                  fullWidth
                  marginTop={1}
                >
                  <Button
                    size="small"
                    color="primary"
                    startIcon={<Trash2 size={14} />}
                    onClick={() =>
                      setState((prev) => ({
                        ...prev,
                        endpoints: prev.endpoints.filter((se) => se.id !== e.id)
                      }))
                    }
                  >
                    {t('Remove')}
                  </Button>
                </FlexContainer>
              </EndpointContainer>
            );
          })}
          <Button
            color="primary"
            onClick={() =>
              setState((x) => ({
                ...x,
                endpoints: [
                  ...x.endpoints,
                  {
                    id: shortid(),
                    name: '',
                    url: '',
                    username: '',
                    password: ''
                  }
                ]
              }))
            }
            startIcon={<PlusCircle size={16} />}
          >
            {t('Add another endpoint')}
          </Button>
        </EndpointsContainer>
      </DialogContentWithoutPadding>
      <DialogActionsWithSlots
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={() => removeWorkdayConfig(config.data.orgId)}
            pending={`${t('Removing')}...`}
            disabled={!endpoints.length}
          >
            {t('Remove')}
          </ButtonWithPromise>
        }
        right={
          <>
            <Button color="primary" onClick={onClose}>
              {t('Close')}
            </Button>
            <ButtonWithPromise
              variant="contained"
              color="primary"
              onClick={() =>
                callFirebaseFunction(
                  'integrations_workday-verifyCredentialsAndStore',
                  {
                    orgId: config.data.orgId,
                    endpoints: state.endpoints
                  }
                ).then(onSuccessfulIntegration)
              }
              disabled={
                !areEndpointsValidAndChanged(
                  state.endpoints,
                  config.data.endpoints
                )
              }
              pending={`${t('Saving')}...`}
            >
              {t('Save')}
            </ButtonWithPromise>
          </>
        }
      />
    </Dialog>
  );
};

export const WorkdayRaasSettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { id: userId, org } = useCurrentUser();
  const [config] = useWorkdayConfig(org.id, userId);
  const cfg = config || null;

  return (
    <IntegrationConfigurationButton
      functionalities={functionalities}
      name={NAME}
      logoUrl="/images/integrations-logos/workday.svg"
      active={!!cfg?.data.active}
      interactive={interactive}
      renderSetupDialog={({
        open,
        onClose,
        onFailure,
        onSuccessfulIntegration
      }: IntegrationSetupDialogProperties) => (
        <SetupDialog
          config={cfg}
          open={open}
          onClose={onClose}
          onFailure={onFailure}
          onSuccessfulIntegration={onSuccessfulIntegration}
        />
      )}
    />
  );
};
